import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import usePageTransition from '../hooks/usePageTransition';

const PageHeader = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.secondary} 100%);
  padding: ${({ theme }) => `${theme.space['3xl']} 0`};
  color: ${({ theme }) => theme.colors.white};
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('/assets/images/pattern.svg');
    opacity: 0.1;
    z-index: 1;
  }
  
  .container {
    position: relative;
    z-index: 2;
  }
`;

const PageTitle = styled(motion.h1)`
  margin-bottom: ${({ theme }) => theme.space.md};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: 3.5rem;
  background: linear-gradient(135deg, #fff 0%, ${({ theme }) => theme.colors.accent} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
`;

const PageDescription = styled(motion.p)`
  max-width: 700px;
  margin: 0 auto;
  font-size: ${({ theme }) => theme.fontSizes.xl};
  line-height: 1.6;
  font-family: ${({ theme }) => theme.fonts.body};
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 350px));
  gap: ${({ theme }) => theme.space.xl};
  margin-top: ${({ theme }) => theme.space.xl};
  margin-bottom: ${({ theme }) => theme.space.xl};
  width: 100%;
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 350px));
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: minmax(280px, 1fr);
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const BlogCard = styled(motion.article)`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: ${({ theme }) => theme.transitions.normal};
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    
    img {
      transform: scale(1.05);
    }
  }
`;

const BlogImageContainer = styled.div`
  overflow: hidden;
  width: 100%;
  height: 200px;
`;

const BlogImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
`;

const BlogContent = styled.div`
  padding: ${({ theme }) => theme.space.lg};
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const BlogMeta = styled.div`
  display: flex;
  margin-bottom: ${({ theme }) => theme.space.sm};
  color: ${({ theme }) => theme.colors.grey[600]};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const BlogDate = styled.span`
  margin-right: ${({ theme }) => theme.space.md};
`;

const BlogCategory = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

const BlogTitle = styled.h3`
  margin-bottom: ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  
  a {
    color: ${({ theme }) => theme.colors.dark};
    text-decoration: none;
    transition: ${({ theme }) => theme.transitions.fast};
    
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const BlogExcerpt = styled.p`
  color: ${({ theme }) => theme.colors.grey[700]};
  margin-bottom: ${({ theme }) => theme.space.md};
  flex-grow: 1;
`;

const blogPosts = [
  {
    id: 1,
    title: "7 AI-Powered Marketing Tactics That Will Transform Your Business",
    excerpt: "Discover how AI is revolutionizing digital marketing and how you can leverage these powerful tools to stay ahead of the competition.",
    date: "May 18, 2025",
    category: "AI Marketing",
    image: "https://images.unsplash.com/photo-1593508512255-86ab42a8e620?q=80&w=500&auto=format&fit=crop"
  },
  {
    id: 2,
    title: "The Future of Data Visualization: Beyond Traditional Dashboards",
    excerpt: "Explore cutting-edge data visualization techniques that are helping businesses extract more meaningful insights from their data.",
    date: "May 10, 2025",
    category: "Business Intelligence",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=500&auto=format&fit=crop"
  },
  {
    id: 3,
    title: "Customer Journey Mapping: A Data-Driven Approach",
    excerpt: "Learn how to create comprehensive customer journey maps using data analytics to identify pain points and opportunities.",
    date: "May 03, 2025",
    category: "Strategic Consulting",
    image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=500&auto=format&fit=crop"
  },
  {
    id: 4,
    title: "How to Build an Effective Marketing Funnel with Analytics",
    excerpt: "A step-by-step guide to creating a high-converting marketing funnel using data analytics to optimize each stage.",
    date: "April 25, 2025",
    category: "Digital Marketing",
    image: "https://images.unsplash.com/photo-1533750349088-cd871a92f312?q=80&w=500&auto=format&fit=crop"
  },
  {
    id: 5,
    title: "The Role of Machine Learning in Customer Segmentation",
    excerpt: "Explore how machine learning algorithms can help you create more precise customer segments for targeted marketing.",
    date: "April 18, 2025",
    category: "AI Marketing",
    image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?q=80&w=500&auto=format&fit=crop"
  },
  {
    id: 6,
    title: "Social Media Analytics: Metrics That Actually Matter",
    excerpt: "Cut through the noise and focus on the social media metrics that truly impact your business growth and ROI.",
    date: "April 10, 2025",
    category: "Digital Marketing",
    image: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?q=80&w=500&auto=format&fit=crop"
  }
];

const Blog: React.FC = () => {
  // Use our custom hook for optimized page transitions
  const { isReturnVisit, isPageReady } = usePageTransition();
  const prefersReducedMotion = useReducedMotion();
  
  // Optimize animations based on user preferences and return visits
  const animationSettings = {
    // If user prefers reduced motion or is a return visitor, use simpler animations
    initial: prefersReducedMotion || isReturnVisit ? { opacity: 0 } : { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0 },
    transition: { 
      type: 'spring', 
      stiffness: prefersReducedMotion || isReturnVisit ? 100 : 50, 
      damping: prefersReducedMotion || isReturnVisit ? 20 : 10,
      duration: prefersReducedMotion || isReturnVisit ? 0.2 : undefined
    }
  };
  
  // Preload blog images
  useEffect(() => {
    // Only preload the first 3 images to improve performance
    blogPosts.slice(0, 3).forEach(post => {
      const img = new Image();
      img.src = post.image;
    });
  }, []);
  // Don't render until the page is ready
  if (!isPageReady) {
    return <div style={{ height: '100vh' }} />; // Empty placeholder while loading
  }
  
  return (
    <>
      <PageHeader>
        <div className="container">
          <PageTitle
            initial={animationSettings.initial}
            animate={animationSettings.animate}
            transition={animationSettings.transition}
          >
            Blog & Insights
          </PageTitle>
          <PageDescription
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 50, damping: 10, delay: 0.2 }}
          >
            Stay updated with the latest digital transformation trends, analytics insights, and industry news
          </PageDescription>
        </div>
      </PageHeader>
      
      <Section id="blog">
        <motion.div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <BlogGrid>
          {blogPosts.map((post, index) => (
            <BlogCard
              key={post.id}
              initial={{ opacity: 0, y: isReturnVisit ? 10 : 30, scale: isReturnVisit ? 0.98 : 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ 
                type: 'spring', 
                stiffness: isReturnVisit ? 100 : 50, 
                damping: isReturnVisit ? 20 : 15, 
                delay: isReturnVisit ? 0.05 * (index % 3) : 0.1 * (index % 3) 
              }}
              whileHover={{ y: -10, transition: { duration: 0.3 } }}
            >
              <BlogImageContainer>
                <BlogImage src={post.image} alt={post.title} />
              </BlogImageContainer>
              <BlogContent>
                <BlogMeta>
                  <BlogDate>{post.date}</BlogDate>
                  <BlogCategory>{post.category}</BlogCategory>
                </BlogMeta>
                <BlogTitle>
                  <Link to={`/blog/${post.id}`}>{post.title}</Link>
                </BlogTitle>
                <BlogExcerpt>{post.excerpt}</BlogExcerpt>
                <Button variant="text" as={Link} to={`/blog/${post.id}`}>
                  Read More →
                </Button>
              </BlogContent>
            </BlogCard>
          ))}
          </BlogGrid>
        </motion.div>
      </Section>
    </>
  );
};

export default Blog;
